'use client'
import {
	Sheet,
	SheetClose,
	SheetContent,
	SheetHeader,
	SheetTitle,
	SheetTrigger,
} from '@/components/ui/sheet'
import Cookies from 'js-cookie'
import Image from 'next/image'
import { usePathname, useRouter } from 'next/navigation'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import CabinetButton from '../app/(home)/profile/_components/cabinet-button'
import { CreditCard, LogOut, Menu, Settings, UserCog, UserRound, X } from 'lucide-react'
import { brand } from '@/config/brand'
import { useAccount, useSubscriptions } from '@/hooks/queries'
import { useLogout } from '@/hooks/use-logout'
import { hasConfigAccess } from '@/services/payments.service'
import { cn } from '@/lib/utils'
import { PageContainer } from '@/components/page-container'

// Иконка на пункт кабинетного меню — по ключу href, чтобы не тащить иконку в данные пункта.
const cabinetIcons: Record<string, typeof UserRound> = {
	'/profile': UserRound,
	'/profile/subscriptions': CreditCard,
	'/profile/configuration': Settings,
	'/profile/edit': UserCog,
}

// Обычный useEffect применяет hasToken уже после того, как браузер отрисовал кадр
// с исходным (неавторизованным) состоянием — на кабинетных страницах это видно как
// вспышку публичных кнопок «Войти»/«Начать бесплатно» поверх личного кабинета.
// useLayoutEffect коммитит новое состояние до отрисовки кадра, поэтому вспышки нет;
// на сервере useLayoutEffect — no-op с предупреждением в консоль, поэтому на SSR
// используем обычный useEffect (там всё равно нет document.cookie).
const useIsomorphicLayoutEffect =
	typeof window !== 'undefined' ? useLayoutEffect : useEffect

export default function Header() {
	const navItems = [
		{ title: 'Преимущества', href: '/#features' },
		{ title: 'Тарифный план', href: '/#tariffs' },
		{ title: 'Отзывы', href: '/#reviews' },
	]

	// Аутентификацию определяем по react-query запросу аккаунта, а не по куке
	// isAuth: она живёт 12ч (expires:1/2) и протухает раньше сессии, из-за чего
	// при перезагрузке появлялась кнопка «Войти», хотя refresh-токен ещё валиден.
	// Запрос /account включаем только при наличии токена сессии (иначе аноним ловил
	// бы лишний 401); пока он грузится/успешен — считаем пользователя авторизованным.
	const [hasToken, setHasToken] = useState(false)
	// До первого прогона эффекта (SSR-рендер и самый первый клиентский кадр до
	// гидратации) мы вообще не знаем, есть ли сессия — authChecked это отличает
	// от «точно нет токена». Используется, чтобы на публичных страницах не
	// показывать на миг «Войти»/«Начать бесплатно» залогиненному пользователю
	// (см. isCabinet-ветки ниже — там та же вспышка решена через isCabinet ? null).
	const [authChecked, setAuthChecked] = useState(false)
	useIsomorphicLayoutEffect(() => {
		setHasToken(!!Cookies.get('refreshToken') || !!Cookies.get('accessToken'))
		setAuthChecked(true)
	}, [])
	const { isError } = useAccount({ enabled: hasToken })
	const auth = hasToken && !isError

	// Блокирующий скрипт в app/layout.tsx уже проставил data-session на <html> по
	// самому факту наличия куки — это видно с первого кадра, ещё до React. Здесь
	// досогласовываем атрибут с итоговым auth (учитывает и провал /account, не
	// только наличие куки) — если токен оказался невалиден, атрибут переключится
	// на 'no' и CSS сам скроет кабинетные элементы / покажет анонимные, без
	// дополнительного JS-условия в разметке. authChecked в зависимостях — чтобы
	// не затирать верную догадку скрипта дефолтным auth=false до первой проверки.
	useIsomorphicLayoutEffect(() => {
		if (!authChecked) return
		document.documentElement.setAttribute('data-session', auth ? 'yes' : 'no')
	}, [authChecked, auth])

	const router = useRouter()
	const pathname = usePathname()
	const isCabinet = pathname?.startsWith('/profile') ?? false
	const handleLogout = useLogout()

	// Пункты настроек/конфигурации в кабинете видны только при активной подписке —
	// та же проверка, что раньше прятала пункт в сайдбаре.
	const { data: subsData } = useSubscriptions({ enabled: hasToken && isCabinet })
	const hasActiveSub = hasConfigAccess(subsData)

	const cabinetNavItems = [
		{ href: '/profile', title: 'Реферальная программа', show: true, exact: true },
		{ href: '/profile/subscriptions', title: 'Подписка', show: true },
		{ href: '/profile/configuration', title: brand.copy.sidebarConfigLabel, show: hasActiveSub },
		{ href: '/profile/edit', title: 'Редактировать аккаунт', show: true },
	].filter(item => item.show)

	// Текущая страница: точное совпадение для «Личного кабинета» (иначе бы он
	// подсвечивался на всех вложенных /profile/*), для остальных — и вложенные пути.
	const isCurrent = (href: string, exact?: boolean) =>
		exact ? pathname === href : pathname === href || pathname?.startsWith(href + '/')

	// Плавающий градиентный индикатор активной кнопки навбара кабинета: сам по себе
	// не рисует общую «дорожку» — только фон текущей кнопки, который плавно
	// переезжает к следующей при смене вкладки (без видимого общего контейнера).
	const navRef = useRef<HTMLElement>(null)
	const itemRefs = useRef<Record<string, HTMLAnchorElement | null>>({})
	const [indicatorStyle, setIndicatorStyle] = useState<{ left: number; top: number; width: number; height: number } | null>(null)

	useLayoutEffect(() => {
		const measure = () => {
			const activeItem = cabinetNavItems.find(item => isCurrent(item.href, item.exact))
			const activeEl = activeItem ? itemRefs.current[activeItem.href] : null
			if (activeEl && navRef.current) {
				const navRect = navRef.current.getBoundingClientRect()
				const itemRect = activeEl.getBoundingClientRect()
				setIndicatorStyle({
					left: itemRect.left - navRect.left,
					top: itemRect.top - navRect.top,
					width: itemRect.width,
					height: itemRect.height,
				})
			}
		}
		measure()
		window.addEventListener('resize', measure)
		return () => window.removeEventListener('resize', measure)
		// auth в зависимостях специально: при первом маунте кабинетная нав ещё не
		// отрисована (auth=false), itemRefs пустые — measure() ничего не находит.
		// Без auth в зависимостях эффект не перезапускался бы в момент, когда auth
		// становится true и пункты меню наконец появляются в DOM, поэтому плашка
		// «зависала» без подсветки до следующей смены pathname/hasActiveSub.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pathname, hasActiveSub, auth])

	const handleNavigation = (e: React.MouseEvent, href: string) => {
		e.preventDefault()
		if (href.startsWith('#')) {
			const element = document.querySelector(href)
			if (element) {
				element.scrollIntoView({
					behavior: 'smooth',
					block: 'start',
				})
			}
		} else {
			router.push(href)
		}
	}
	const side = 'top'
	return (
		<div id='app-header' className='relative top-0 left-0 right-0 bg-black z-50'>
			<PageContainer>
				<div className='relative flex  xl:flex-row items-center justify-between min-h-[5rem] py-4 xl:py-0'>
					{/* Logo */}
					<div
						className='flex-shrink-0'
						onClick={() => (window.location.href = '/')}
					>
						<Image
							src={brand.assets.logo}
							alt='logo'
							width={80}
							height={80}
							className='w-auto h-12'
						/>
					</div>

					{/* Desktop Navigation. isCabinet сам по себе не мигает (известен и на
					    сервере, и на клиенте — берётся из pathname), поэтому структуру
					    ветвим по нему одному. А вот то, что зависит от сессии (кабинетная
					    нав целиком / «Войти» / «Личный кабинет» vs «Начать бесплатно»),
					    рендерим ВСЕГДА и переключаем видимость через data-session-show —
					    CSS-правила в globals.css решают по data-session на <html>, который
					    блокирующий скрипт в layout.tsx проставляет ещё до первого кадра. */}
					{isCabinet ? (
						<div className='contents' data-session-show='yes'>
							<nav
								ref={navRef}
								className='relative hidden items-center gap-2 xl:absolute xl:left-1/2 xl:top-1/2 xl:flex xl:-translate-x-1/2 xl:-translate-y-1/2'
							>
								{indicatorStyle && (
									<div
										className='pointer-events-none absolute rounded-full bg-gradient-to-r from-blue-500 to-purple-600 transition-[transform,width,height] duration-300 ease-out'
										style={{
											transform: `translate(${indicatorStyle.left}px, ${indicatorStyle.top}px)`,
											width: indicatorStyle.width,
											height: indicatorStyle.height,
										}}
									/>
								)}
								{cabinetNavItems.map(item => {
									const Icon = cabinetIcons[item.href]
									const active = isCurrent(item.href, item.exact)
									return (
										<a
											key={item.href}
											ref={el => {
												itemRefs.current[item.href] = el
											}}
											href={item.href}
											onClick={e => handleNavigation(e, item.href)}
											className={cn(
												'relative z-10 flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors duration-200 lg:text-base',
												active
													? 'text-white'
													: 'text-gray-400 hover:bg-white/5 hover:text-gray-200'
											)}
										>
											{Icon && <Icon size={16} />}
											{item.title}
										</a>
									)
								})}
							</nav>
							<button
								onClick={handleLogout}
								title='Выйти'
								aria-label='Выйти'
								className='hidden h-10 w-10 shrink-0 items-center justify-center rounded-full text-gray-400 transition-colors duration-200 hover:bg-red-500/10 hover:text-red-400 xl:flex'
							>
								<LogOut size={18} />
							</button>
						</div>
					) : (
						<nav className='hidden xl:flex items-center space-x-4'>
							{navItems.map(item => (
								<a
									key={item.title}
									href={item.href}
									onClick={e => handleNavigation(e, item.href)}
									className='text-white px-4 py-2 rounded-full border border-white/20 hover:bg-white/10 transition-all duration-300 text-sm lg:text-base'
								>
									{item.title}
								</a>
							))}
							<a
								data-session-show='no'
								onClick={e =>
									window.location.href = '/auth/fastregister?type=enter'
									// handleNavigation(e, '/auth/fastregister?type=enter')
								}
								className='text-white px-4 py-2 rounded-full border border-white/20 hover:bg-white/10 transition-all duration-300 text-sm lg:text-base cursor-pointer'
							>
								Войти
							</a>
							<CabinetButton data-session-show='yes' />
							<button
								data-session-show='no'
								className='bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-2 rounded-full hover:opacity-90 transition-opacity duration-300 text-sm lg:text-base'
								onClick={() => window.location.href = '/auth/fastregister'
									// router.push('/auth/fastregister')
							}
							>
								Начать бесплатно
							</button>
						</nav>
					)}

					{/* Mobile Navigation */}
					<Sheet>
						<SheetTrigger className='text-white xl:hidden'>
							<Menu />
						</SheetTrigger>
						<SheetContent side={side} className='bg-black/80 px-4' showClose={false}>
							<SheetHeader className='flex-row items-center justify-between space-y-0 text-left'>
								<SheetTitle className='text-white'>
									{isCabinet && auth ? 'Личный кабинет' : 'Меню'}
								</SheetTitle>
								<SheetClose
									className='rounded-sm text-white/70 opacity-70 transition-opacity hover:text-white hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-2 focus:ring-offset-black disabled:pointer-events-none data-[state=open]:bg-white/10'
									aria-label='Закрыть меню'
								>
									<X className='h-5 w-5' />
								</SheetClose>
							</SheetHeader>
							{isCabinet ? (
								<div className='flex flex-col gap-1 py-4 text-white' data-session-show='yes'>
									{cabinetNavItems.map(item => {
										const Icon = cabinetIcons[item.href]
										const active = isCurrent(item.href, item.exact)
										return (
											<SheetClose key={item.href} asChild>
												<button
													onClick={() => router.push(item.href)}
													className={cn(
														'flex items-center gap-3 rounded-xl px-4 py-3 text-left text-base transition-colors duration-200',
														active
															? 'bg-gradient-to-r from-blue-500/20 to-purple-600/20 text-white'
															: 'text-gray-400 hover:bg-white/5 hover:text-gray-200'
													)}
												>
													{Icon && <Icon size={18} className={active ? 'text-purple-300' : 'text-gray-500'} />}
													{item.title}
												</button>
											</SheetClose>
										)
									})}
									<div className='my-2 h-px bg-white/10' />
									<SheetClose asChild>
										<button
											onClick={handleLogout}
											className='flex items-center gap-3 rounded-xl px-4 py-3 text-left text-base text-red-400 transition-colors duration-200 hover:bg-red-500/10'
										>
											<LogOut size={18} />
											Выйти
										</button>
									</SheetClose>
								</div>
							) : (
								<div className='flex flex-col gap-4 py-6 text-white items-center '>
									<SheetClose>
										<div onClick={() => router.push(navItems[0].href)}>
											{navItems[0].title}
										</div>
									</SheetClose>
									<SheetClose>
										<div onClick={() => router.push(navItems[1].href)}>
											{navItems[1].title}
										</div>
									</SheetClose>
									<SheetClose>
										<div onClick={() => router.push(navItems[2].href)}>
											{navItems[2].title}
										</div>
									</SheetClose>
									<SheetClose asChild>
										<button
											data-session-show='no'
											className='px-6 py-2 rounded-full hover:opacity-90 transition-opacity duration-300 text-sm lg:text-base'
											onClick={() => window.location.href = '/auth/fastregister?type=enter'
												// router.push('/auth/fastregister?type=enter')
										}
										>
											Войти
										</button>
									</SheetClose>
									<SheetClose asChild>
										<CabinetButton data-session-show='yes' />
									</SheetClose>
									<SheetClose asChild>
										<button
											data-session-show='no'
											className='bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-2 rounded-full hover:opacity-90 transition-opacity duration-300 text-sm lg:text-base'
											onClick={() => window.location.href = '/auth/fastregister'
												// router.push('/auth/fastregister')
										}
										>
											Начать бесплатно
										</button>
									</SheetClose>
								</div>
							)}
						</SheetContent>
					</Sheet>
					{/* Mobile menu button */}
				</div>
			</PageContainer>
		</div>
	)
}
